import { useState, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

interface ComboboxInputProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** false = solo permite valores de la lista; true (default) = texto libre permitido */
  freeText?: boolean;
}

export function ComboboxInput({
  options,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  freeText = true,
}: ComboboxInputProps) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState<number | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const filtered = value.trim() === ''
    ? options
    : options.filter(o => o.toLowerCase().includes(value.toLowerCase()));

  const showDropdown = open && filtered.length > 0;

  useEffect(() => {
    if (open && anchorRef.current) {
      setWidth(anchorRef.current.offsetWidth);
    }
  }, [open]);

  function handleSelect(opt: string) {
    onChange(opt);
    setOpen(false);
  }

  function handleBlur() {
    setTimeout(() => {
      if (!freeText && !options.includes(value)) {
        onChange('');
      }
      setOpen(false);
    }, 150);
  }

  return (
    <Popover.Root open={showDropdown}>
      <Popover.Anchor asChild>
        <div ref={anchorRef}>
          <input
            ref={inputRef}
            value={value}
            onChange={e => { onChange(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={className}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          sideOffset={4}
          style={{ width }}
          className="z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto"
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={() => setOpen(false)}
        >
          {filtered.map(opt => (
            <button
              key={opt}
              type="button"
              onMouseDown={e => { e.preventDefault(); handleSelect(opt); }}
              onTouchEnd={e => { e.preventDefault(); handleSelect(opt); }}
              className={cn(
                'w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors',
                value === opt && 'bg-primary-50 text-primary-700 font-medium',
              )}
            >
              {opt}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
